import { useEffect, useState } from "react";
import { X, Hammer, Repeat, UtensilsCrossed, CalendarDays, Pin, Sparkles, Wrench, BookOpen, Hand, Clock, Users, Star, ShoppingCart, Check } from "lucide-react";
import api from "@/lib/api";
import { dueMeta, pinTimeLabel } from "@/lib/labels";

const iso = (d) => d.toISOString().slice(0, 10);
const relDate = (isoStr, kt = (s) => s, loc = "nl-NL") => {
  if (!isoStr) return "";
  const d = new Date(isoStr); const t = new Date();
  t.setHours(0, 0, 0, 0); d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - t) / 86400000);
  const dm = d.toLocaleDateString(loc, { day: "numeric", month: "short" });
  if (diff === 0) return kt("Vandaag");
  if (diff === 1) return `${kt("Morgen")} ${dm}`;
  return d.toLocaleDateString(loc, { weekday: "short", day: "numeric", month: "short" });
};

const PRIO = { spoed: { label: "Spoed", cls: "#EF4444" }, urgent: { label: "Urgent", cls: "#F59E0B" }, geen_haast: { label: "Geen haast", cls: "#10B981" } };
const KLUS_STATUS = { open: "Open", bezig: "In behandeling", afgerond: "Afgerond" };

const AvatarChip = ({ name, url, theme }) => (
  url
    ? <img src={url} alt={name} title={name} className="w-8 h-8 rounded-full object-cover border-2" style={{ borderColor: theme.vars["--k-panel"] }} />
    : <span title={name} className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-head font-black k-accent border-2" style={{ borderColor: theme.vars["--k-panel"] }}>{name?.[0]?.toUpperCase()}</span>
);

export default function KioskMore({ moduleKey, title, kt, loc, theme, onClose, onAction, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/kiosk/module/${moduleKey}`).then((r) => { if (alive) { setData(r.data); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [moduleKey, reloadKey]);

  const Empty = ({ text }) => <p className="k-muted font-head font-bold py-8 text-center">{text}</p>;

  const renderKlusjes = () => (
    (data.items || []).length === 0 ? <Empty text={kt("Geen open klusjes 🎉")} /> : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.items.map((k) => {
          const p = PRIO[k.priority] || PRIO.geen_haast;
          return (
            <div key={k.id} data-testid={`more-klus-${k.id}`} className="k-pill rounded-2xl p-4">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-head font-bold text-white" style={{ background: p.cls }}>{kt(p.label)}</span>
                <span className="text-xs font-head font-bold k-muted">{kt(KLUS_STATUS[k.status] || k.status)}</span>
              </div>
              <h3 className="font-head font-black text-lg leading-tight">{k.title}</h3>
              {k.description && <p className="text-sm opacity-80 mt-0.5 line-clamp-2">{k.description}</p>}
              {k.assigned && <p className="text-xs font-bold mt-1 inline-flex items-center gap-1" style={{ color: "var(--k-accent)" }}><Hand className="w-3.5 h-3.5" strokeWidth={2.5} />{kt("Opgepakt door")} {k.assigned_name || kt("iemand")}</p>}
              {!k.assigned && (
                <button data-testid={`more-klus-take-${k.id}`} onClick={() => onAction({ action: "klusTake", klusId: k.id, title: k.title })}
                  className="mt-2 w-full k-accent brutal-press rounded-full px-3 py-2 font-head font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
                  <Hand className="w-4 h-4" strokeWidth={2.5} />{kt("Ik pak dit op")}
                </button>
              )}
            </div>
          );
        })}
      </div>
    )
  );

  const renderRuilmarkt = () => (
    (data.items || []).length === 0 ? <Empty text={kt("Geen openstaande taakruilingen")} /> : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.items.map((s) => (
          <div key={s.id} data-testid={`more-swap-${s.id}`} className="k-pill rounded-2xl p-4">
            <h3 className="font-head font-black text-lg leading-tight">{s.task_title}</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {(s.task_next_date || s.task_due_when) && <span className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-2 py-0.5 k-accent"><Clock className="w-3.5 h-3.5" strokeWidth={2.5} />{s.task_next_date ? relDate(s.task_next_date, kt, loc) : kt(dueMeta(s.task_due_when).label)}</span>}
              <span className="text-xs k-muted font-bold">{s.requester_name || kt("Iemand")}</span>
            </div>
            <button data-testid={`more-swap-take-${s.id}`} onClick={() => onAction({ action: "takeSwap", swapId: s.id, title: s.task_title })}
              className="mt-2 w-full k-accent brutal-press rounded-full px-3 py-2 font-head font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
              <Hand className="w-4 h-4" strokeWidth={2.5} />{kt("Ja, ik doe dit wel")}
            </button>
          </div>
        ))}
      </div>
    )
  );

  const renderEten = () => (
    <div className="space-y-5">
      <div>
        <h3 className="font-head font-black text-lg mb-2 flex items-center gap-2"><UtensilsCrossed className="w-5 h-5" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />{kt("Recepten")}</h3>
        {(data.recipes || []).length === 0 ? <Empty text={kt("Nog geen recepten")} /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {data.recipes.map((r) => (
              <div key={r.id} data-testid={`more-recipe-${r.id}`} className="k-pill rounded-2xl overflow-hidden">
                {r.image_url && <img src={r.image_url} alt="" className="w-full h-24 object-cover" />}
                <div className="p-2.5">
                  <p className="font-head font-bold leading-tight line-clamp-2">{r.name}</p>
                  {r.rating != null && <p className="text-xs font-bold mt-0.5 inline-flex items-center gap-1" style={{ color: "var(--k-accent)" }}><Star className="w-3.5 h-3.5" strokeWidth={2.5} />{r.rating}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <h3 className="font-head font-black text-lg mb-2 flex items-center gap-2"><ShoppingCart className="w-5 h-5" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />{kt("Boodschappenlijst")}</h3>
        {(data.shopping || []).length === 0 ? <Empty text={kt("Boodschappenlijst is leeg")} /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.shopping.map((it) => (
              <div key={it.id} className="k-pill rounded-xl px-3 py-2 flex items-center justify-between">
                <span className="font-head font-bold">{it.name}</span>
                {it.quantity && <span className="text-sm k-muted font-bold">{it.quantity}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderRooster = () => {
    const items = data.items || [];
    if (items.length === 0) return <Empty text={kt("Geen taken ingepland")} />;
    const today = iso(new Date());
    const groups = {};
    items.forEach((t) => { const d = t.next_date || today; (groups[d] = groups[d] || []).push(t); });
    const days = Object.keys(groups).sort();
    return (
      <div className="space-y-4">
        {days.map((d) => (
          <div key={d}>
            <p className="font-head font-black text-sm capitalize mb-1.5" style={{ color: "var(--k-accent)" }}>{relDate(d, kt, loc)}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {groups[d].map((t) => {
                const meta = dueMeta(t.due_when); const Icon = meta.icon;
                return (
                  <div key={t.id} data-testid={`more-rooster-${t.id}`} className="k-pill rounded-2xl p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-xs font-bold font-head opacity-80"><Icon className="w-3.5 h-3.5" strokeWidth={3} />{t.due_when === "specifiek" ? t.due_time : kt(meta.label)}</span>
                      <div className="flex -space-x-2">{(t.assignees || []).slice(0, 3).map((a, i) => <AvatarChip key={i} name={a.name} url={a.avatar_url} theme={theme} />)}</div>
                    </div>
                    <h3 className="font-head font-bold text-base mt-1 leading-tight">{t.title}</h3>
                    {t.assignee_names?.length > 0 && <p className="text-xs k-muted font-bold mt-0.5">{t.assignee_names.join(", ")}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderEvents = () => (
    (data.items || []).length === 0 ? <Empty text={kt("Nog niks gepland")} /> : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.items.map((it) => (
          <div key={it.id} data-testid={`more-content-${it.id}`} className="k-pill rounded-2xl p-4">
            {it.image_url && <img src={it.image_url} alt="" className="w-full h-28 object-cover rounded-xl mb-2" />}
            <h3 className="font-head font-black text-lg leading-tight">{it.title}</h3>
            {it.event_date && <p className="text-xs font-bold mt-0.5 inline-flex items-center gap-1" style={{ color: "var(--k-accent)" }}><CalendarDays className="w-3.5 h-3.5" strokeWidth={2.5} />{relDate(it.event_date, kt, loc)}{pinTimeLabel(it) ? ` · ${pinTimeLabel(it)}` : ""}</p>}
            {it.description && <p className="text-sm opacity-80 mt-1 line-clamp-3">{it.description}</p>}
            {(it.min_participants || it.max_participants) && (
              <p className="text-xs font-bold mt-1 inline-flex items-center gap-1 k-muted"><Users className="w-3.5 h-3.5" strokeWidth={2.5} />
                {it.participants?.length || 0}{it.max_participants ? `/${it.max_participants}` : ""} {kt("deelnemers")}{it.min_participants ? ` · ${kt("min.")} ${it.min_participants}` : ""}</p>
            )}
            {it.participants?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {it.participants.map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1 k-accent rounded-full pl-1 pr-2.5 py-0.5 text-xs font-head font-bold">
                    <span className="w-5 h-5 rounded-full bg-white/40 flex items-center justify-center text-[10px] font-black">{p.name?.[0]?.toUpperCase()}</span>{p.name}
                  </span>
                ))}
              </div>
            )}
            {data.can_participate && it.allow_participation !== false && (
              <button data-testid={`more-participate-${it.id}`} onClick={() => onAction({ action: "participate", itemId: it.id, module: moduleKey, title: it.title })}
                className="mt-2 w-full k-accent brutal-press rounded-full px-3 py-2 font-head font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
                <Hand className="w-4 h-4" strokeWidth={2.5} />{kt("Ik doe mee")}
              </button>
            )}
          </div>
        ))}
      </div>
    )
  );

  const renderKennisbank = () => (
    (data.items || []).length === 0 ? <Empty text={kt("Nog geen artikelen")} /> : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.items.map((it) => (
          <div key={it.id} data-testid={`more-kb-${it.id}`} className="k-pill rounded-2xl p-4">
            {it.category && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-head font-bold k-accent mb-1">{it.category}</span>}
            <h3 className="font-head font-black text-lg leading-tight">{it.title}</h3>
            {it.description && <p className="text-sm opacity-80 mt-1 whitespace-pre-wrap">{it.description}</p>}
            {it.steps?.length > 0 && (
              <ol className="list-decimal list-inside text-sm opacity-80 mt-1 space-y-0.5">
                {it.steps.map((s, i) => <li key={i}>{typeof s === "string" ? s : s?.text}</li>)}
              </ol>
            )}
          </div>
        ))}
      </div>
    )
  );

  const ICONS = { klusjes: Hammer, ruilmarkt: Repeat, eten: UtensilsCrossed, rooster: CalendarDays, pindates: Pin, activiteiten: Sparkles, onderhoud: Wrench, kennisbank: BookOpen };
  const TitleIcon = ICONS[moduleKey] || Sparkles;

  const body = () => {
    if (loading) return <p className="k-muted font-head font-black text-xl animate-pulse py-10 text-center">{kt("Laden…")}</p>;
    if (!data) return <Empty text={kt("Kon dit overzicht niet laden")} />;
    switch (moduleKey) {
      case "klusjes": return renderKlusjes();
      case "ruilmarkt": return renderRuilmarkt();
      case "eten": return renderEten();
      case "rooster": return renderRooster();
      case "kennisbank": return renderKennisbank();
      default: return renderEvents();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-start justify-center p-4 sm:p-6 overflow-y-auto" onClick={onClose} data-testid="kiosk-more-overview">
      <div className="k-card rounded-[2rem] w-full max-w-3xl my-4" style={theme.vars} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-5 sm:p-6 pb-3 sticky top-0 k-card rounded-t-[2rem] z-10">
          <h2 className="font-head font-black text-2xl flex items-center gap-2.5"><TitleIcon className="w-7 h-7" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />{kt(title)}</h2>
          <button data-testid="kiosk-more-close" onClick={onClose} className="k-pill brutal-press rounded-full p-2.5 shrink-0"><X className="w-6 h-6" strokeWidth={2.5} /></button>
        </div>
        <div className="px-5 sm:px-6 pb-6">{body()}</div>
      </div>
    </div>
  );
}
